import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import { formatDate, formatNumber } from "../../../utils/format.ts";
import type { ProspectContactCrmStatus } from "../../../api/client";

interface CrmStatusCellProps {
  status?: ProspectContactCrmStatus;
}

/**
 * Two chips per contact: where they are in the outreach cadence, and where
 * they are in the funnel. These are different things — a sequence is a rep's
 * 1:1 follow-up cadence, the deal stage is the actual pipeline position — so
 * they are shown side by side rather than merged into one status.
 */
export default function CrmStatusCell({ status }: CrmStatusCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!status || !status.crm_contact_id) {
    return (
      <Typography variant="caption" color="text.disabled">
        Not in CRM
      </Typography>
    );
  }

  const { sequence, deal } = status;
  const replied = !!sequence?.last_replied_at;

  // "Ended" only counts when they were enrolled at some point; a contact the
  // portal has simply never touched shows nothing rather than a false negative.
  const sequenceLabel = sequence?.is_enrolled
    ? "In sequence"
    : sequence?.ended_at
      ? "Sequence ended"
      : null;

  if (!sequenceLabel && !deal?.stage && !replied) {
    return (
      <Typography variant="caption" color="text.disabled">
        No activity
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}
      onClick={(e) => e.stopPropagation()}
    >
      {sequenceLabel && (
        <Chip
          size="small"
          label={sequenceLabel}
          color={sequence?.is_enrolled ? "primary" : "default"}
          variant={sequence?.is_enrolled ? "filled" : "outlined"}
          clickable
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ height: 20 }}
        />
      )}
      {replied && (
        <Chip size="small" label="Replied" color="success" sx={{ height: 20 }} />
      )}
      {deal?.stage && (
        <Chip
          size="small"
          label={deal.stage}
          color="info"
          variant="outlined"
          clickable
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ height: 20 }}
        />
      )}

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 220 }}>
          {sequence && (sequence.is_enrolled || sequence.ended_at) && (
            <>
              <Typography variant="subtitle2">
                {sequence.sequence_name ?? "Sequence"}
              </Typography>
              {sequence.enrolled_at && (
                <Typography variant="caption">
                  Enrolled {formatDate(sequence.enrolled_at)}
                </Typography>
              )}
              {sequence.ended_at && (
                <Typography variant="caption">
                  Ended {formatDate(sequence.ended_at)}
                </Typography>
              )}
              {sequence.enrolled_by_email && (
                <Typography variant="caption" color="text.secondary">
                  Sending as {sequence.enrolled_by_email}
                </Typography>
              )}
              {sequence.last_replied_at && (
                <Typography variant="caption" color="success.main">
                  Replied {formatDate(sequence.last_replied_at)}
                </Typography>
              )}
            </>
          )}
          {deal?.stage && (
            <>
              <Typography variant="subtitle2" sx={{ mt: sequence ? 1 : 0 }}>
                {deal.deal_name ?? "Deal"}
              </Typography>
              <Typography variant="caption">
                {deal.stage}
                {deal.pipeline_name ? ` · ${deal.pipeline_name}` : ""}
              </Typography>
              {deal.stage_entered_at && (
                <Typography variant="caption" color="text.secondary">
                  Since {formatDate(deal.stage_entered_at)}
                </Typography>
              )}
              {deal.amount != null && (
                <Typography variant="caption" color="text.secondary">
                  {formatNumber(deal.amount)}
                  {deal.close_date ? ` · closes ${formatDate(deal.close_date)}` : ""}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
